import axios from 'axios';

const SHOPIFY_STORE_DOMAIN = '0prqvu-0y.myshopify.com';
const SHOPIFY_STOREFRONT_TOKEN =
  // biome-ignore lint/style/noNonNullAssertion: Automated migration to Biome
  process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN!;

// Documentation and version information https://shopify.dev/docs/api/storefront?version=2025-04
const API_VERSION = '2025-04';
const ENDPOINT = `https://${SHOPIFY_STORE_DOMAIN}/api/${API_VERSION}/graphql.json`;

export type CartItemInput = {
  merchandiseId: string;
  quantity: number;
};

export type CartLine = {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    image?: {
      url: string;
      altText?: string;
    };
    product?: {
      title: string;
    };
    price?: {
      amount: string;
      currencyCode: string;
    };
  };
};

export type Cart = {
  id: string;
  checkoutUrl: string;
  lines: {
    edges: { node: CartLine }[];
  };
  cost: {
    subtotalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
};

export const storefrontClient = axios.create({
  baseURL: ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
  },
});

export async function runStorefrontQuery<T = unknown>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const res = await storefrontClient.post('', { query, variables });

  if (res.data.errors) {
    console.error('Shopify Storefront API Errors:', res.data.errors);
    throw new Error('Storefront API returned errors');
  }

  return res.data.data;
}

const CART_FRAGMENT = `
  id
  checkoutUrl
  lines(first: 10) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            image {
              url
              altText
            }
            price {
              amount
              currencyCode
            }
            product {
              title
            }
          }
        }
      }
    }
  }
  cost {
    subtotalAmount {
      amount
      currencyCode
    }
  }
`;

export async function createCart(items: CartItemInput[]): Promise<Cart> {
  const query = `
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          ${CART_FRAGMENT}
        }
        userErrors { field message }
      }
    }
  `;

  const variables = {
    input: {
      lines: items.map(({ merchandiseId, quantity }) => ({
        merchandiseId,
        quantity,
      })),
    },
  };

  const data = await runStorefrontQuery<{ cartCreate: { cart: Cart } }>(
    query,
    variables
  );
  return data.cartCreate.cart;
}

export async function addToCart(
  cartId: string,
  items: CartItemInput[]
): Promise<Cart> {
  const query = `
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ${CART_FRAGMENT}
        }
        userErrors { field message }
        warnings { message }
      }
    }
  `;

  const variables = { cartId, lines: items };
  const data = await runStorefrontQuery<{ cartLinesAdd: { cart: Cart } }>(
    query,
    variables
  );
  return data.cartLinesAdd.cart;
}

export async function removeFromCart(
  cartId: string,
  lineItemIds: string[]
): Promise<Cart> {
  const query = `
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ${CART_FRAGMENT}
        }
        userErrors { field message }
        warnings { message }
      }
    }
  `;

  const variables = { cartId, lineIds: lineItemIds };
  const data = await runStorefrontQuery<{ cartLinesRemove: { cart: Cart } }>(
    query,
    variables
  );
  return data.cartLinesRemove.cart;
}

export async function updateCartLines(
  cartId: string,
  updates: { id: string; quantity: number }[]
): Promise<Cart> {
  const query = `
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ${CART_FRAGMENT}
        }
        userErrors { field message }
        warnings { message }
      }
    }
  `;

  const variables = { cartId, lines: updates };
  const data = await runStorefrontQuery<{ cartLinesUpdate: { cart: Cart } }>(
    query,
    variables
  );
  return data.cartLinesUpdate.cart;
}

export async function getCart(cartId: string): Promise<Cart> {
  const query = `
    query CartQuery($cartId: ID!) {
      cart(id: $cartId) {
        ${CART_FRAGMENT}
      }
    }
  `;

  const variables = { cartId };
  const data = await runStorefrontQuery<{ cart: Cart }>(query, variables);
  return data.cart;
}
